import type { useI18n } from '@affine/i18n';

import { ConnectionApiError } from './connection-api-error';
import type { ConnectionDependent } from './types';

type I18n = ReturnType<typeof useI18n>;

/** Reasons a service names when it refuses a connection, in the user's words. */
const REASON_KEYS = {
  unauthorized: 'com.streamlain.connections.reason.unauthorized',
  unreachable: 'com.streamlain.connections.reason.unreachable',
  per_signature_approval:
    'com.streamlain.connections.reason.per-signature-approval',
  pin_rejected: 'com.streamlain.connections.reason.pin-rejected',
  insecure_url: 'com.streamlain.connections.reason.insecure-url',
  bad_response: 'com.streamlain.connections.reason.bad-response',
} as const;

export type ConnectionFailureReason = keyof typeof REASON_KEYS;

export function isConnectionFailureReason(
  reason: string | undefined | null
): reason is ConnectionFailureReason {
  return Boolean(reason && Object.hasOwn(REASON_KEYS, reason));
}

/** What the service objected to, or `null` when it named no known reason. */
export function connectionFailureReasonLabel(
  t: I18n,
  reason: string | undefined | null
): string | null {
  return isConnectionFailureReason(reason) ? t[REASON_KEYS[reason]]() : null;
}

/** Why a connection request failed, as precise as the service told it. */
export function describeConnectionFailure(t: I18n, cause: unknown): string {
  if (cause instanceof ConnectionApiError) {
    return connectionFailureReasonLabel(t, cause.reason) ?? cause.message;
  }
  return cause instanceof Error
    ? cause.message
    : t['com.streamlain.connections.operation-failed']();
}

const DEPENDENT_KEYS = {
  signature_certificate: 'com.streamlain.connections.dependent.certificates',
  signature_signing_profile:
    'com.streamlain.connections.dependent.signing-profiles',
} as const;

function dependentKey(type: string) {
  return Object.hasOwn(DEPENDENT_KEYS, type)
    ? DEPENDENT_KEYS[type as keyof typeof DEPENDENT_KEYS]
    : 'com.streamlain.connections.dependent.other';
}

/** What goes with a connection on removal, e.g. "2 certificates". */
export function describeConnectionDependents(
  t: I18n,
  dependents: readonly ConnectionDependent[]
): string {
  return dependents
    .map(dependent =>
      t[dependentKey(dependent.type)]({ count: dependent.count })
    )
    .join(', ');
}
